import { Component, Emit, Inject, Prop, Watch } from 'vue-property-decorator';
import ApeironComponent from '@/components/commons/apeiron-component.model';
import { Solucion } from '@/shared/model/solucion.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import ParticipanteService from '@/components/participantes/participante.service';
import { IPersona } from '@/shared/model/persona/persona.model';
import { IParticipacionSnp, MenuInstitucionSnp, MenuSedeSnp, MenuTipoBeca, ParticipacionSnp } from '@/shared/model/snp/alumno.model';
import { RolAutoridad } from '@/shared/model/enumerations/rol-autoridad.model';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';
import { obtenerFechaMasMesesUltimoDiaMes, obtenerFechaPrimerDiaMes } from '@/shared/date/date-utils';
import { FiltroParticipacion } from '@/shared/model/filtro-participacion.model';
import { Participante } from '@/components/participantes/participante.model';
import * as mesUtils from '@/shared/date/mes-utils';

const validations = function () {
  return {
    alumnoForm: {
      participante: {
        cvu: { required },
        curp: { required },
        nombre: { required },
        apellidoPaterno: { required },
        apellidoMaterno: {},
        duracionMeses: {
          required,
          minValue: minValue(1),
        },
        tiempoDedicacion: { required },
        tipoBeca: { required },
        promedioAnterior: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(100),
        },
        inicioGeneracion: { required },
        inicioEstudios: { required },
        finEstudios: { required },
        institucion: { required },
        sede: { required },
        comprobanteNacionalidad: {},
      },
    },
  };
};

@Component({ validations: validations })
export default class MatriculaRegistroComponent extends ApeironComponent {
  @Inject('participanteService') private readonly participanteService: () => ParticipanteService;

  @Prop({ required: true })
  public solucion: Solucion;

  @Prop({ required: true })
  public solicitud: Solicitud;

  @Prop({
    required: false,
    default: function () {
      return new ParticipacionSnp();
    },
  })
  public alumnoEditar: ParticipacionSnp;

  public tiposBeca: Array<MenuTipoBeca> = [];

  public alumnoForm: IParticipacionSnp = new ParticipacionSnp();
  public tiempoDedicacion = 'Completo';

  public instituciones: Array<MenuInstitucionSnp> = [];
  public sedes: Array<MenuSedeSnp> = [];
  public mesUtils = mesUtils;

  public seleccionarAlumno(alumno: IPersona): void {
    this.validarAlumnoDuplicado(alumno.cvu).then(existeCvu => {
      if (existeCvu) {
        this.alertService().showError(this, this.$t('apeironGwApp.matriculaGpln.form.alumnoDuplicado').toString());
      } else {
        this.iniciarParticipacionDeAlumno(alumno);
      }
    });
  }

  private iniciarParticipacionDeAlumno(alumno: IPersona): void {
    this.alumnoForm = new ParticipacionSnp();
    this.alumnoForm.solicitudId = this.solicitud.id;
    this.alumnoForm.solucionId = this.solicitud.solucionId;
    this.alumnoForm.rol = RolAutoridad.ALUMNO;
    this.alumnoForm.participante = Participante.fromObject(alumno);
    this.alumnoForm.participante.duracionMeses = this.solicitud?.programa?.planDeEstudios?.duracionOficial ?? 0;
    this.alumnoForm.participante.clavePrograma = this.solicitud.programa.clave;
    this.alumnoForm.participante.tiempoDedicacion = '1';
    this.alumnoForm.participante.tipoBeca = this.tiposBeca[0].value;
    if (this.instituciones.length == 1) {
      this.alumnoForm.participante.institucion = this.instituciones[0].value;
      this.seleccionarInstitucion();
    }
  }

  public mounted(): void {
    this.iniciarComponente(false);
  }

  @Watch('alumnoEditar')
  public watchAlumnoEditar(): void {
    this.iniciarComponente(false);
  }

  public limpiarDatos(): void {
    this.iniciarComponente(true);
  }

  public iniciarComponente(limpiarAlumno: boolean): void {
    this.tiposBeca = [];
    this.tiposBeca.push({
      value: { clave: 'POSTULANTE_BECA', descripcion: 'Postulante a Beca SECIHTI' },
      text: 'Postulante a Beca SECIHTI',
    });
    this.instituciones = [];

    if (this.solicitud?.programa?.instituciones) {
      this.solicitud.programa.instituciones.forEach(institucion => {
        this.instituciones.push({
          value: {
            claveInstitucion: institucion.claveInstitucion,
            nombreInstitucion: institucion.nombreInstitucion,
          },
          text: institucion.nombreInstitucion,
        });
      });
    }

    if (this.alumnoEditar?.id && !limpiarAlumno) {
      this.alumnoForm = { ...this.alumnoEditar };
      this.seleccionarInstitucion();
    } else {
      this.alumnoForm = new ParticipacionSnp();
    }
    this.alumnoForm.participante.duracionMeses = this.solicitud?.programa?.planDeEstudios?.duracionOficial;
  }

  public guardarAlumno(): void {
    if (!this.formularioIsInvalid) {
      if (!this.readOnly) {
        this.alumnoForm.contenedorId =
          this.alumnoForm.participante.institucion.claveInstitucion + '-' + this.alumnoForm.participante.sede.claveSede;

        if (this.alumnoForm.id) {
          this.participanteService()
            .update(this.alumnoForm)
            .then(() => {
              this.registrarAlumno();
            })
            .catch(error => {
              this.manejaErrorRegistro(error);
            });
        } else {
          this.participanteService()
            .create(this.alumnoForm, this.solucion.id)
            .then(() => {
              this.registrarAlumno();
            })
            .catch(error => {
              this.manejaErrorRegistro(error);
            });
        }
      }
    } else {
      this.alertService().showWarning(this, this.$t('apeironGwApp.matriculaGpln.form.formularioInvalido').toString());
    }
  }

  private manejaErrorRegistro(error: any): void {
    if (error.response?.data?.message == 'error.conflict') {
      this.alertService().showError(this, this.$t('apeironGwApp.persona.form.messages.solicitudActiva').toString());
    } else if (error.response?.data?.message == 'error.serviceUnavailable') {
      this.alertService().showError(this, this.$t('apeironGwApp.persona.form.messages.historicoApoyosNoDisponible').toString());
    } else if (error.response?.data?.message == 'error.apoyoSimilarHistoricoBecas') {
      this.alertService().showError(this, this.$t('apeironGwApp.persona.form.messages.apoyoSimilar').toString());
    } else if (error.response?.data?.message == 'error.participanteRegistrado') {
      this.alertService().showError(this, this.$t('apeironGwApp.persona.form.messages.participanteRegistradoPrograma').toString());
    } else if (error.response?.data?.message == 'error.apoyoVigenteHistoricoBecas') {
      this.alertService().showError(
        this,
        this.$t('apeironGwApp.persona.form.messages.apoyoVigente', { param: error.response?.data?.detail }).toString()
      );
    } else {
      this.alertService().showHttpError(this, error.response.message ? error.response.message : error.response);
    }
  }

  @Emit('guardarAlumno')
  public registrarAlumno(): ParticipacionSnp {
    const alumno = ParticipacionSnp.fromObject(this.alumnoForm);

    this.iniciarComponente(true);

    return alumno;
  }

  public seleccionarFechaInicioGeneracion(): void {
    this.alumnoForm.participante.inicioEstudios = obtenerFechaPrimerDiaMes(this.alumnoForm.participante.inicioGeneracion);
    this.alumnoForm.participante.finEstudios = obtenerFechaMasMesesUltimoDiaMes(
      this.alumnoForm.participante.inicioGeneracion,
      this.alumnoForm.participante.duracionMeses
    );
  }

  public seleccionarInstitucion(): void {
    this.sedes = [];

    if (this.solicitud?.programa?.instituciones) {
      this.solicitud.programa.instituciones.forEach(institucion => {
        if (institucion.claveInstitucion == this.alumnoForm.participante.institucion?.claveInstitucion) {
          institucion.sedes.forEach(sede => {
            this.sedes.push({
              value: {
                claveSede: sede.claveSede,
                claveDependencia: sede.claveDependencia,
                nombreDependencia: sede.nombreDependencia,
                sede: sede.sede,
              },
              text: `${sede.nombreDependencia} (${sede.sede})`,
            });
          });
        }
      });
    }

    if (!this.alumnoForm.participante?.sede) {
      if (this.sedes.length == 1) {
        this.alumnoForm.participante.sede = this.sedes[0].value;
      } else {
        this.alumnoForm.participante.sede = undefined;
      }
    }
  }

  public validarAlumnoDuplicado(cvu: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const filtroPart: FiltroParticipacion = {
        solicitudId: this.solicitud.id,
        rol: RolAutoridad.ALUMNO,
        participanteCvu: cvu,
      };
      const paginationQuery = {
        page: 0,
        size: 1,
        sort: ['id,desc'],
        filter: filtroPart,
      };
      this.participanteService()
        .obtenerParticipaciones(paginationQuery)
        .then(res => resolve((res.data?.length ?? 0) > 0))
        .catch(() => reject(new Error()));
    });
  }

  /**
   * Indica si el formulario completo es inválido.
   *
   * El formulario se considera inválido cuando:
   * - Existen errores de validación definidos por Vuelidate (`$v.$invalid`).
   * - El periodo de estudios no cumple con la regla de negocio establecida en `periodoEstudiosValido()`.
   * - La persona participante no cumple con la restricción de género definida para la convocatoria (`debeRestringirRegistroPorGenero`).
   *
   * Esta validación centraliza las reglas estructurales del formulario y las
   * reglas de negocio que determinan si la solicitud puede enviarse.
   *
   * @returns {boolean} `true` si el formulario es inválido; de lo contrario, `false`.
   */
  get formularioIsInvalid(): boolean {
    return this.$v.$invalid || !this.periodoEstudiosValido() || this.debeRestringirRegistroPorGenero;
  }

  /**
   * Valida que la fecha de término de estudios cumpla con la vigencia establecida
   * en la configuración de la convocatoria.
   *
   * La validación toma como referencia la fecha de inicio de beca configurada
   * mediante `fechaRefInicioBeca` y calcula el rango permitido de acuerdo con:
   *
   * - La vigencia mínima configurada en `vigenciaMinimaMeses`.
   * - La vigencia máxima configurada en `vigenciaMaximaMeses`.
   *
   * La fecha de término de estudios debe encontrarse dentro del rango permitido,
   * considerando como límite inferior la fecha de inicio de beca más la vigencia
   * mínima, y como límite superior la fecha de inicio de beca más la vigencia máxima.
   *
   * La fecha de referencia debe tener el formato `dd/mm/yyyy`.
   *
   * @returns {boolean} `true` si la fecha de fin de estudios se encuentra dentro
   * del periodo permitido; `false` si está fuera del rango establecido, no existe
   * fecha de término o no se encuentra configurada la información necesaria.
   *
   * @example
   * Configuración:
   * - fechaRefInicioBeca: `01/08/2026`
   * - vigenciaMinimaMeses: `6`
   * - vigenciaMaximaMeses: `48`
   *
   * Fechas válidas:
   * - `01/02/2027` (cumple vigencia mínima)
   * - `01/08/2030` (cumple vigencia máxima)
   *
   * Fechas inválidas:
   * - `01/01/2027` (menor a la vigencia mínima)
   * - `01/09/2030` (mayor a la vigencia máxima)
   */
  public periodoEstudiosValido(): boolean {
    const fechaFinStr = this.alumnoForm.participante.finEstudios;
    if (!fechaFinStr) {
      return false;
    }
    const [dia, mes, anio] = fechaFinStr.split('/').map(Number);
    const fechaFin = new Date(anio, mes - 1, dia);

    const fechaInicioBeca = this.solucion.params.fechaRefInicioBeca;
    const vigenciaMinimaMeses = this.solucion.params.vigenciaMinimaMeses;
    const vigenciaMaximaMeses = this.vigenciaMaximaMeses;
    if (!fechaInicioBeca || !vigenciaMinimaMeses) {
      return false;
    }
    const [diaInicio, mesInicio, anioInicio] = fechaInicioBeca?.split('/').map(Number);

    // Inicio de la beca: Agosto 2026
    const inicioBeca = new Date(anioInicio, mesInicio - 1, diaInicio);

    const fechaMinima = new Date(inicioBeca);
    fechaMinima.setMonth(fechaMinima.getMonth() + vigenciaMinimaMeses);
    fechaMinima.setDate(fechaMinima.getDate() - 1);

    // Vigencia máxima: 48 meses
    const fechaMaxima = new Date(inicioBeca);
    fechaMaxima.setMonth(fechaMaxima.getMonth() + vigenciaMaximaMeses);
    fechaMaxima.setDate(fechaMaxima.getDate() - 1);

    return fechaFin >= fechaMinima && fechaFin <= fechaMaxima;
  }

  /**
   * Obtiene la información del periodo de vigencia de estudios correspondiente
   * a la convocatoria.
   *
   * La fecha de inicio de la beca se obtiene de los parámetros de configuración
   * mediante `fechaRefInicioBeca`, con formato `dd/mm/yyyy`. Para generar una
   * descripción legible se consideran únicamente el mes y el año de dicha fecha.
   *
   * La vigencia mínima se obtiene directamente del parámetro
   * `vigenciaMinimaMeses`.
   *
   * La vigencia máxima se obtiene mediante el getter `vigenciaMaximaMeses`,
   * el cual utiliza `vigenciaMaximaMeses` configurado en la solución y,
   * cuando no está disponible, toma como referencia la duración oficial
   * del plan de estudios.
   *
   * @returns Objeto con la información del periodo de vigencia de la beca.
   *
   * @property {string} fechaInicio
   * Mes y año de inicio de la beca.
   *
   * @property {number} vigenciaMinima
   * Cantidad mínima de meses permitidos para la vigencia de los estudios,
   * de acuerdo con la configuración de la convocatoria.
   *
   * @property {number} vigenciaMaxima
   * Cantidad máxima de meses permitidos para la vigencia de los estudios.
   * Corresponde al valor configurado para la convocatoria o, en su ausencia,
   * a la duración oficial del plan de estudios.
   *
   * @example
   * Si la configuración contiene:
   * fechaRefInicioBeca = `01/08/2026`
   * vigenciaMinimaMeses = 6
   * vigenciaMaximaMeses = 48
   *
   * Retorna:
   * {
   *   fechaInicio: "agosto de 2026",
   *   vigenciaMinima: 6,
   *   vigenciaMaxima: 48
   * }
   *
   * @example
   * Si `vigenciaMaximaMeses` no está configurada y la duración oficial
   * del plan de estudios es de 48 meses, retorna:
   *
   * {
   *   fechaInicio: "agosto de 2026",
   *   vigenciaMinima: 6,
   *   vigenciaMaxima: 48
   * }
   */
  get periodoBecaTexto() {
    const fechaInicioBeca = this.solucion.params.fechaRefInicioBeca;
    const vigenciaMinimaMeses = this.solucion.params.vigenciaMinimaMeses;
    const vigenciaMaximaMeses = this.vigenciaMaximaMeses;
    const [, mesInicio, anioInicio] = fechaInicioBeca?.split('/').map(Number) || [];
    return {
      fechaInicio: `${this.mesUtils.obtenerNombreMes(mesInicio)} de ${anioInicio}`,
      vigenciaMinima: vigenciaMinimaMeses,
      vigenciaMaxima: vigenciaMaximaMeses,
    };
  }

  /**
   * Indica si debe restringirse el registro para la convocatoria MMF
   * cuando la persona participante no pertenece al género femenino.
   *
   * @returns `true` si la solución corresponde a la convocatoria MMF y
   * el género de la persona participante es distinto de "Femenino";
   * en caso contrario, `false`.
   */
  get debeRestringirRegistroPorGenero() {
    return this.solucion?.tags?.includes('mmf') && this.alumnoForm.participante.genero !== 'Femenino';
  }

  /**
   * Obtiene la vigencia máxima de la beca expresada en meses.
   *
   * La vigencia se determina con el siguiente orden de prioridad:
   * 1. `solucion.params.vigenciaMaximaMeses`, cuando está definida.
   * 2. `solicitud.programa.planDeEstudios.duracionOficial`, como valor alternativo.
   * 3. `0`, si ninguno de los valores anteriores está disponible.
   *
   * @returns La vigencia máxima en meses.
   */
  get vigenciaMaximaMeses() {
    return this.solucion.params.vigenciaMaximaMeses
      ? this.solucion.params.vigenciaMaximaMeses
      : this.solicitud?.programa?.planDeEstudios?.duracionOficial ?? 0;
  }
}
